import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { setSwitchCredentials } from '../../pages/configuration/customerSwitchUserSlice';
import { axiosInstance } from '../../services/apiInterceptor';
import secureLocalStorage from 'react-secure-storage';
import { toast } from 'react-toastify';
import ToastComponent from '../../components/toast';

type UseInstanceProps = {
  onClose?: () => void;
};

type Tenant = {
  cid: number;
  tenantId: string;
  roleId: string;
  instanceName?: string;
  instanceId?: string;
};

const isDebug = process.env.REACT_APP_SECURE_LOCAL_STORAGE_ENABLE === 'false';
const storage = isDebug ? localStorage : secureLocalStorage;

function useInstance(instanceProps?: UseInstanceProps) {
  // const { handleRequestWithRole } = useWealthlaneCustomerApi();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState<boolean>(false);

  const onSwitch = (props?: {
    id: string;
    isCompanyChanged?: boolean;
    isHomeCompany?: boolean;
    switchableRole?: string;
    instanceName?: string;
    instanceId?: string;
  }) => {
    const { id, isCompanyChanged, isHomeCompany, switchableRole } = props;
    if (isHomeCompany) {
      onKillInstance();
      return;
    }
    setLoading(true);
    axiosInstance
      .get(
        `${process.env.REACT_APP_WEALTHLANE_API_ENDPOINT}/api/identity/api/instance/switch/${id}?switchRoleId=${switchableRole}`,
        {
          headers: {
            'X-Tenant-Id': getTenantById()?.tenantId ?? '',
            'X-Switch-Source-Role-Id': switchableRole ?? '',
          },
        }
      )
      .then((res: any) => {
        const response = res.data;
        if (response != null) {
          dispatch(
            setSwitchCredentials({
              tenantId: response?.token,
              isOrigin: '',
            })
          );
          let tenant: Tenant = {
            cid: getAllTenants().length + 1,
            tenantId: response?.token,
            roleId: switchableRole,
            instanceName: props.instanceName,
            instanceId: props.instanceId,
          };
          tenant = pushTenant(tenant);

          instanceProps?.onClose && instanceProps?.onClose();
          window.location.href = `/#cid=${tenant.cid}`;
          // window.location.reload();
          setLoading(false);
        }
      })
      .catch((error) => {
        console.log('error', error);
        toast.error(
          <ToastComponent
            label={'Error'}
            message={`${String(error?.response?.data?.error?.message ?? '')}`}
          />,
          {
            toastId: 1,
            autoClose: false,
          }
        );
        setLoading(false);
      })
      .finally(() => {
        setLoading(false);
        instanceProps?.onClose && instanceProps?.onClose();
      });
  };

  const getCurrentCid = () => {
    const hash = window.location.hash;
    const cid = +hash.replace('#', '').replace('cid=', '');
    return cid;
  };

  const getAllTenants = () => {
    const tenants = (JSON.parse((storage.getItem('tenants') as string) || '[]') ||
      []) as unknown as Tenant[];
    return tenants;
  };

  const getTenantById = (cid: number = getCurrentCid()) => {
    const tenants = getAllTenants();
    return tenants.find((tenant) => tenant.cid === cid);
  };

  const getRoleId = (cid: number = getCurrentCid()) => {
    const tenant = getTenantById(cid);
    return tenant?.roleId;
  };

  const getInstance = (cid: number = getCurrentCid()) => {
    const { instanceId, instanceName } = getTenantById(cid);
    return { instanceId, instanceName };
  };

  const getTenantByTenantId = (tenantId: string) => {
    const tenants = getAllTenants();
    return tenants.find((tenant) => tenant.tenantId === tenantId);
  };

  const pushTenant = (tenant: Tenant) => {
    const tenants = getAllTenants();
    const existingTenantByInstance = tenants.find(
      (t) => t.instanceId === tenant.instanceId && t.roleId === tenant.roleId
    );
    if (existingTenantByInstance) {
      return existingTenantByInstance;
    }

    const existingTenantByCid = tenants.find((t) => t.cid === tenant.cid);
    if (existingTenantByCid) {
      return existingTenantByCid;
    }

    tenants.push(tenant);
    storage.setItem('tenants', JSON.stringify(tenants));
    return tenant;
  };

  const removeTenant = (cid: number = getCurrentCid()) => {
    const tenants = getAllTenants();
    const filteredTenants = tenants.filter((t) => t.cid !== cid);
    storage.setItem('tenants', JSON.stringify(filteredTenants));
  };

  const onKillInstance = () => {
    window.location.href = '/';
  };

  const isSwitched = (cid: number = getCurrentCid()) => {
    const tenant = getTenantById(cid);
    return tenant;
  };

  return {
    loading,
    isSwitched,
    onSwitch,
    onKillInstance,
    getAllTenants,
    getTenantById,
    getTenantByTenantId,
    pushTenant,
    removeTenant,
    getRoleId,
    getInstance,
    getCurrentCid,
  };
}

export default useInstance;
